import { computed, inject, Injectable, linkedSignal, resource } from '@angular/core';
import { SellOffRepository } from '../repositories/sell-off.repository';
import { CartService } from './cart.service';
import { DateService } from '@softline/core';

export const SELL_OF_TYPES = {
  AUTO: {
    icon: 'fa-regular fa-stop',
    label: 'Automatisch',
  },
  NEVER: {
    icon: 'fa-regular fa-stop',
    label: 'Keiner',
  },
  ALWAYS: {
    icon: 'fa-regular fa-stop',
    label: 'Immer',
  },
} as const;

export type SellOfType = keyof typeof SELL_OF_TYPES;
export type SellOfDisplayData = (typeof SELL_OF_TYPES)[SellOfType];

@Injectable()
export class SellOffService {
  readonly #repository = inject(SellOffRepository);
  readonly #cartService = inject(CartService);
  readonly #dateService = inject(DateService);

  readonly #sellOffResource = resource({
    params: () => this.#cartService.selectedProcess(),
    loader: () => {
      const date = this.#dateService.now();
      return this.#repository.load(date);
    },
  });

  readonly #sellOffValue = computed(() => {
    const cart = this.#cartService.cart();

    if (cart && cart?.sellOff) {
      return cart?.sellOff ?? null;
    }

    if (this.#sellOffResource.hasValue()) {
      return this.#sellOffResource.value();
    }

    return null;
  });

  readonly #isActive = linkedSignal(() => {
    const value = this.#sellOffValue();
    return value?.active ?? false;
  });

  public readonly isEditable = computed(() => {
    const value = this.#sellOffValue();
    return value?.editable ?? false;
  });

  public readonly isActive = this.#isActive.asReadonly();

  public readonly sellOfType = computed(() => {
    return this.#sellOffValue()?.type ?? null;
  });

  public readonly readableSellOfType = computed(() => {
    const type = this.sellOfType();
    if (!type) return null;
    return SELL_OF_TYPES[type]?.label ?? '-';
  });

  async change(value: string): Promise<void> {
    await this.#cartService.updateSellOffType(value);
  }
}
